<template>
    <div>
        <div class="app-reg-info" id="alertRef" v-if="registrationInfo">
            <div v-if="!registrationInfo.registrationInformation || store.state.guestLogin.activeAlert">
                <SystemAlert/>
            </div> 
            <div>
                <p v-if="isBusinessUser && !isLoading && registrationInfo.registrationInformation != null">
                    If there are more than 10 Vehicle registrations on your record you will see 10 registrations listed below. Use the 'Registration search' feature below to look for your registrations 
                    not listed below. <br />
                    <strong>NOTE:</strong> You will only see Vehicles where you are the registered primary owner or you are a lessee for a Leased vehicle. You will not see Leased vehicles for which you are the lessor.
                </p>
            </div>
            
            <!-- Existing Registrations -->
            <div v-if="registrationInfo.registrationInformation">
                <div class="app-reg-info-row" v-for="(regInfo, i) in registrationInfo.registrationInformation" :key="`existing-${i}`">
                    <RegistrationCard 
                        :reg-info="regInfo"
                        @renew="getSelectRenewRegistration"
                        @cancel="getSelectCancelRegistration"
                        @duplicate="getSelectDuplicateRegistration"
                        @toggle-details="handleToggleDetails"
                    />
                </div>
            </div>
            
            <!-- Business User Search -->
            <div class="app-input-group" v-if="isBusinessUser && registrationInfo.registrationInformation != null">
                <h4 class="comm-text">Registration / Vehicle Search 
                    <span>
                        <Tooltip theme="my-custom-tooltip" :triggers="['hover', 'click', 'focus']">
                            <button type="button" class="btn-tooltip-trigger" aria-label="Paperless Notice Details">
                                <img src="@ridmv/web-themes/images/icons/question-circle-solid.svg" alt="Description" class="comm-tooltip">
                            </button>
                            <template #popper>
                                You can search for your unregistered 
                                Vehicles by changing the Vehicle ID type 
                                and search using the Title # or VIN #.
                                <span class="sr-only">You can search for your unregistered Vehicles 
                                by changing the Vehicle ID type and search using the Title # or VIN #.
                                </span>
                            </template>
                        </Tooltip>
                    </span>
                </h4>
                
                <!-- Search Form -->
                <div class="app-input-row" v-if="showVehicleField">
                    <!-- ID Type Dropdown -->
                    <div class="app-input-group">
                        <label :for="typeField">Vehicle ID type</label>
                        <select class="app-custom-select" v-model="myPlateIdType" aria-required="true"
                            autocomplete="off" :id="typeField" :aria-describedby="`${typeField}Desc`">
                            <option value="plate"> Plate # </option>
                            <option value="title"> Title # </option>
                            <option value="vin"> VIN # </option>
                        </select>
                    </div>

                    <!-- Plate Number / Title Number / VIN Input -->
                    <div class="app-input-group" :class="{ 'app-invalid-group': !isPlateValid }">
                        <label :for="plateField">{{ labelText }}</label>
                        <input class="plate" type="text" :maxlength="plateMaxLength" :id="plateField" 
                            :aria-describedby="`${plateField}Desc`"
                            v-model.trim="myPlateNumber" @input="validatePlate" aria-required="true" autocomplete="off"/>
                        <p v-if="!isPlateValid" class="app-error-message">{{ plateError }}</p>
                    </div>

                    <!-- Plate Type Dropdown -->
                    <div class="app-input-group" :class="{ 'app-invalid-group': !isTypeValid }" 
                        v-if="myPlateIdType === 'plate'">
                        <label :for="typeField">Plate type</label>
                        <select class="app-custom-select" :id="typeField"
                            v-model.lazy="myPlateType" @change="validateType" aria-required="true" autocomplete="off">
                            <option :value="{ code: 'none' }">Select a type</option>
                            <option v-for="type in registrationTypes" 
                                :value="{ code: type.vehiclePlateTypeId, description: type.description }" 
                                :key="type.vehiclePlateTypeId">
                                {{ type.description }}
                            </option>
                        </select>
                        <p v-if="!isTypeValid" class="app-error-message">Plate type is required.</p>
                    </div>
                    
                    <!-- Search Button -->
                    <BaseButtonGroup>
                        <button type="button" @click="validate() && getSearchRegistration()"
                            @keydown.enter.prevent="validate() && getSearchRegistration()"
                            @keydown.space.prevent="validate() && getSearchRegistration()" class="app-btn-primary"> Search
                        </button>
                    </BaseButtonGroup>
                </div>
                
                <!-- Search Results Section -->
                <div class="search-results-section" v-if="searchedRegistrationInfo && searchedRegistrationInfo.length > 0">
                    <div class="app-reg-info-row" v-for="(regInfo, i) in searchedRegistrationInfo" :key="`search-${i}`">
                        <RegistrationCard 
                            :reg-info="regInfo"
                            @renew="getSelectRenewRegistration"
                            @cancel="getSelectCancelRegistration"
                            @duplicate="getSelectDuplicateRegistration"
                            @toggle-details="handleToggleDetails"
                        />
                    </div>
                </div>
                <!-- No Results Message -->
                <div v-if="hasSearchedOnce && (!searchedRegistrationInfo || searchedRegistrationInfo.length === 0)" class="no-search-results">
                    No Vehicle found for the search.
                </div>
            </div>
            
            <div v-if="!isLoading && !registrationInfo?.registrationInformation && networkResponse && !networkResponse.message" class="no-records">
                No Registration(s) found.
            </div>
        </div>
        <div v-if="!isLoading && registrationInfo === null && networkResponse && !networkResponse.message">
            No Registration(s) found.
        </div> 
    </div> 
</template>

<script setup>
import { ref, computed, onMounted, nextTick, watch } from 'vue';
import { useStore } from 'vuex';
import * as COMMONS from '../../store/_commons';
import SystemAlert from '@/components/SystemAlert';
import RegistrationCard from './RegistrationCard.vue';
import { Tooltip } from 'floating-vue';
import '@/assets/tooltip.css';
import BaseButtonGroup from '@/components/ui/BaseButtonGroup';

// Use the Vuex store
const store = useStore();

const plateField = ref('plateSearchValue');
const typeField = ref('searchType');

const myPlateIdType = ref('plate'); // plate | title | vin
const plateNumber = ref('');
const titleNumber = ref('');
const vinNumber = ref('');
const myPlateType = ref({ code: 'none' });

const isPlateValid = ref(true);
const isTypeValid = ref(true);

const hasSearchedOnce = ref(false);

const patterns = {
  inputPattern: /^[a-zA-Z0-9]*$/
};

const showVehicleField = computed(() => isBusinessUser.value);

// Reactive state
const alertRef = ref(null);

const lookupInfo = computed(() => store.getters[COMMONS.VEHICLE_LOOKUP_INFO]);

// User type
const isBusinessUser = computed(() => store.state.guestLogin.isBusinessUser);

// Property for both user types
const registrationInfo = computed(() => {
    if (isBusinessUser.value) {
        const businessData = store.getters[COMMONS.BUSINESS_REGISTRATION_INFO];
        return {
            registrationInformation: businessData.businessRegistrationInformation
        };
    }
    return store.getters[COMMONS.GUEST_REGISTRATION_INFO];
});

const networkResponse = computed(() => store.state.networkResponse);

const isLoading = computed(() => store.state.isLoading);

const labelText = computed(() => {
  if (myPlateIdType.value === 'title') return 'Title number';
  if (myPlateIdType.value === 'vin') return 'VIN number';
  return 'Plate number';
});

const plateMaxLength = computed(() => {
  if (myPlateIdType.value === 'title') return 8;
  if (myPlateIdType.value === 'vin') return 17;
  return 7;
});

const myPlateNumber = computed({
  get() {
    if (myPlateIdType.value === 'title') return titleNumber.value;
    if (myPlateIdType.value === 'vin') return vinNumber.value;
    return plateNumber.value;
  },
  set(val) {
    if (myPlateIdType.value === 'title') titleNumber.value = val;
    else if (myPlateIdType.value === 'vin') vinNumber.value = val;
    else plateNumber.value = val;
  }
});

const plateError = computed(() => {
  if (isPlateValid.value) return '';

  if (myPlateIdType.value === 'title') {
    return 'Title number is required. Acceptable characters are letters or numbers. Please do not include dashes or spaces.';
  }

  if (myPlateIdType.value === 'vin') {
    return 'VIN number is required. Acceptable characters are letters or numbers. Please do not include dashes or spaces.';
  }

  return 'Plate number is required. Acceptable characters are letters or numbers. Please do not include dashes or spaces.';
});

const registrationTypes = computed(() => {
  return lookupInfo.value.allRegistrationTypes;
});

const loadRegistrationTypes = async () => {
  if (!registrationTypes.value || registrationTypes.value.length === 0) {
    await store.dispatch(COMMONS.VEHICLE_GET_TYPE_LIST);
  }
};

const searchedRegistrationInfo = computed(() => {
  const searchData = store.getters[COMMONS.BUSINESS_REGISTRATION_SEARCH_INFO];
  return searchData?.businessRegistrationSearchResults || [];
});

watch(myPlateIdType, () => {
    plateNumber.value = '';
    titleNumber.value = '';
    vinNumber.value = '';
    myPlateType.value = { code: 'none' };
    isPlateValid.value = true;
    isTypeValid.value = true;

    // Clear existing search results when search type changes
    hasSearchedOnce.value = false;
    store.dispatch(COMMONS.CLEAR_BUSINESS_REGISTRATION_SEARCH_RESULTS);
});

const validatePlate = () => {
  const val = (myPlateNumber.value || '').trim();
  const isValidChars = patterns.inputPattern.test(val);

  if (myPlateIdType.value === 'vin') {
    isPlateValid.value = val.length > 0 && val.length <= 17 && isValidChars;
    return;
  }

  if (myPlateIdType.value === 'title') {
    isPlateValid.value = val.length > 0 && val.length <= 8 && isValidChars;
    return;
  }

  isPlateValid.value = val.length > 0 && val.length <= 7 && isValidChars;
};

const validateType = () => {
  if (myPlateIdType.value !== 'plate') {
    isTypeValid.value = true;
    return;
  }
  isTypeValid.value = myPlateType.value?.code !== 'none';
};

const validate = () => {
    validatePlate();
    validateType();
    return isPlateValid.value && isTypeValid.value;
};

const getSearchRegistration = async () => {
  if (!validate()) return;

  const searchTypeMap = {
    plate: 1,
    title: 2,
    vin: 3
  };

  const payload = {
    searchType: searchTypeMap[myPlateIdType.value],
    searchValue: myPlateNumber.value.trim(),
    vehiclePlateTypeId: myPlateIdType.value === 'plate' ? Number(myPlateType.value.code) : 0
  };

  await store.dispatch(COMMONS.BUSINESS_SEARCH_REGISTRATION_BY_VEHICLE, payload);
  hasSearchedOnce.value = true;
};

// Toggle details
const handleToggleDetails = (regInfo) => {
    regInfo.showDetails = !regInfo.showDetails;
};

// Methods
const getSelectRenewReg = (renewRegValue) => {
    store.dispatch(COMMONS.GUEST_SELECTRENEWREGISTRATION, renewRegValue);
};

const getRenewRegistrationEligibility = () => {
    store.dispatch(COMMONS.GUEST_RENEWREGISTRATION);
};

const getDuplicateRegistrationEligibility = () => {
    store.dispatch(COMMONS.GUEST_DUPLICATEREGISTRATION);
};

const getCancelRegistrationEligibility = () => {
    store.dispatch(COMMONS.GUEST_CANCELREGISTRATION);
};

const getRenewRegistration = () => {
    getRenewRegistrationEligibility();
};

const getDuplicateRegistration = () => {
    getDuplicateRegistrationEligibility();
};

const getCancelRegistration = () => {
    getCancelRegistrationEligibility();
};

const getSelectRenewRegistration = async (renewRegValue) => {
    getSelectRenewReg(renewRegValue);
    await getRenewRegistration();
    store.state.guestLogin.step = 1;
};

const getSelectDuplicateRegistration = async (renewRegValue) => {
    getSelectRenewReg(renewRegValue);
    await getDuplicateRegistration();
    store.state.guestLogin.step = 1;
};

const getSelectCancelRegistration = async (renewRegValue) => {
    getSelectRenewReg(renewRegValue);
    await getCancelRegistration();
    store.state.guestLogin.step = 1;
};

// Lifecycle hook
onMounted(async () => {
    window.scroll(0, 0);

    // Load registration types for plate type dropdown (business users only)
    if (isBusinessUser.value) {
        await loadRegistrationTypes();
    }

    nextTick(() => {
        if (alertRef.value) {
            alertRef.value.focus();
        }
    });
});

const resetSearchSection = () => {
  myPlateIdType.value = 'plate';
  plateNumber.value = '';
  titleNumber.value = '';
  vinNumber.value = '';
  myPlateType.value = { code: 'none' };
  isPlateValid.value = true;
  isTypeValid.value = true;
  hasSearchedOnce.value = false;
  store.dispatch(COMMONS.CLEAR_BUSINESS_REGISTRATION_SEARCH_RESULTS);
};

defineExpose({
  resetSearchSection
});
</script>


<style scoped lang="scss">
    .app-reg-info-row {
        margin: 15px 0px 18px 0px;
        align-items: center;
        border-bottom: 1px solid #F2F2F2;
        padding-bottom: 2rem;
        gap: 4rem;
        
        span, label {
            font-size: 1rem;
        }
    }
    
    .app-reg-info-row:nth-child(odd) {
        background-color: #F2F2F2;
    }

    .no-records {
        font-weight: 400;
    }

    .app-input-group {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        
        .comm-text {
            display: flex;
            font-weight: 600;
            margin-bottom: 10px;
            .btn-tooltip-trigger{
                min-width: 25px;
                border: none;
                background-color: unset;
                border-radius: unset;
                padding: 0rem;
                
                .comm-tooltip{
                    width: 18px;
                    margin-bottom: -2px;
                }
            }
        }

        .app-input-group.app-invalid-group input ~ p, .app-input-group.app-invalid-group select ~ p {
            color: var(--ri-red);
            width: var(--control-width);
        }

        .no-search-results {
            margin-top: 1rem;
            font-weight: 600;
        }
    }

    .app-input-row {
        display: flex;
        flex-wrap: wrap;
        gap: 1rem;
        align-items: flex-start;
        justify-content: flex-start;
        .app-btn-primary{
            margin-top: 1rem;
        }
    }
    .search-row {
        display: flex;
        flex-wrap: wrap;
        gap: 1rem;
        align-items: flex-start;
        margin-bottom: 1.5rem;
    }

    .search-action-col {
        display: flex;
        align-items: flex-end;
        padding-bottom: 1rem;
        .search-reg {
            height: fit-content;
        }
    }

    .search-results-section {
        margin-top: 1.5rem;
        padding-top: 1rem;
        border-top: 1px solid #F2F2F2;
    }

    .search-results-title {
        margin-bottom: 0.75rem;
        font-size: 1rem;
        font-weight: 700;
        color: var(--ri-dark-blue);
    }
</style>